import { Trans } from '@lingui/react/macro';
import {
  FacilityTransferDirection,
  type FacilityTransferItemPublicRecord,
  WheelExternalCheckStatus
} from '@navio/nsdk';
import { NoddiBadge, NoddiBasicCard, NoddiButton } from 'noddi-ui-web';
import { type CarProps, formatInTimeZone, getCarDisplayName, NoddiDateFormats } from 'noddi-util';
import { NavioLicensePlate } from 'noddi-web';
import { useState } from 'react';
import { getCommonTranslations } from '../../utils/commonTranslations';
import { getExternalCheckStatusLabel } from './externalCheckStatus';
import { useFacilityTimeZone } from './facilityTimeZone';
import { IssueDialog } from './IssueDialog';
import { useSaveExternalChecks } from './useSaveExternalChecks';

interface Props {
  item: FacilityTransferItemPublicRecord;
  direction: FacilityTransferDirection;
}

export const WheelSetItemCard = ({ item, direction }: Props) => {
  const timeZone = useFacilityTimeZone();
  const [isIssueDialogOpen, setIsIssueDialogOpen] = useState(false);

  const {
    mutate: saveCheck,
    isPending: isSaving,
    error: saveError,
    reset: resetSave,
    publicSlug
  } = useSaveExternalChecks();
  const isEditable = item.isEditable;
  const orderSlug = item.orderSlug.toUpperCase();

  const car: CarProps = {
    make: item.carMake,
    model: item.carModel,
    licensePlate: item.licensePlate
  };
  const carName = getCarDisplayName({ car, showLicensePlate: false, showYear: false });
  const status = item.externalCheckStatus;
  const isReady = status === WheelExternalCheckStatus.READY;
  const comment = item.externalCheckComment.trim();
  const completedTime = item.completedAt ? formatInTimeZone(item.completedAt, NoddiDateFormats.TIME, timeZone) : null;

  const setStatus = (nextStatus: WheelExternalCheckStatus) => {
    saveCheck({ publicSlug, items: [{ itemId: item.id, status: nextStatus }] });
  };

  const openIssueDialog = () => {
    resetSave();
    setIsIssueDialogOpen(true);
  };

  const saveIssue = (issueComment: string) => {
    saveCheck(
      { publicSlug, items: [{ itemId: item.id, status: WheelExternalCheckStatus.ISSUE, comment: issueComment }] },
      { onSuccess: () => setIsIssueDialogOpen(false) }
    );
  };

  return (
    <li>
      <NoddiBasicCard size='small' className='grid grid-cols-[1fr_auto] gap-3'>
        <div className='flex min-w-0 flex-col gap-1 sm:row-span-2'>
          <NavioLicensePlate
            car={car}
            season={item.tireSeason ?? null}
            seasonTranslations={getCommonTranslations().seasonBadge}
            className='self-start'
          />
          {carName !== '' && <p className='truncate text-sm'>{carName}</p>}
          {item.ownerName && <p className='truncate text-sm text-muted-foreground'>{item.ownerName}</p>}
          {status === WheelExternalCheckStatus.ISSUE && comment !== '' && (
            <p className='rounded-md bg-destructive/5 px-3 py-2 text-sm whitespace-pre-wrap text-muted-foreground'>
              {comment}
            </p>
          )}
          {completedTime != null && (
            <p className='text-sm font-medium text-successDark'>
              {direction === FacilityTransferDirection.OUTGOING ? (
                <Trans>Picked up {completedTime}</Trans>
              ) : (
                <Trans>Delivered {completedTime}</Trans>
              )}
            </p>
          )}
          <p className='mt-1 text-xs text-muted-foreground'>
            <Trans>Order: #{orderSlug}</Trans>
          </p>
        </div>

        <div className='self-start justify-self-end'>
          <StatusBadge status={status} direction={direction} />
        </div>

        {isEditable && (
          <div className='col-span-2 grid auto-cols-fr grid-flow-col gap-2 sm:col-span-1 sm:col-start-2 sm:flex sm:self-end'>
            <NoddiButton
              size='small'
              variant={isReady ? 'outline' : 'primary'}
              className='w-full sm:w-auto'
              loading={isSaving && !isIssueDialogOpen}
              disabled={isSaving}
              onClick={() => setStatus(isReady ? WheelExternalCheckStatus.PENDING : WheelExternalCheckStatus.READY)}
            >
              {isReady ? <Trans>Undo</Trans> : <Trans>Mark as ready</Trans>}
            </NoddiButton>
            <NoddiButton
              size='small'
              variant='outline'
              className='w-full sm:w-auto'
              disabled={isSaving}
              onClick={openIssueDialog}
            >
              {status === WheelExternalCheckStatus.ISSUE ? <Trans>Edit note</Trans> : <Trans>Report issue</Trans>}
            </NoddiButton>
          </div>
        )}
      </NoddiBasicCard>

      {isIssueDialogOpen && (
        <IssueDialog
          item={item}
          initialComment={item.externalCheckComment}
          isSaving={isSaving}
          error={saveError}
          onSave={saveIssue}
          onClose={() => setIsIssueDialogOpen(false)}
        />
      )}
    </li>
  );
};

const STATUS_BADGE_VARIANT: Record<WheelExternalCheckStatus, 'success' | 'destructive' | 'outline'> = {
  READY: 'success',
  ISSUE: 'destructive',
  PENDING: 'outline'
};

const StatusBadge = ({
  status,
  direction
}: {
  status: WheelExternalCheckStatus;
  direction: FacilityTransferDirection;
}) => <NoddiBadge variant={STATUS_BADGE_VARIANT[status]}>{getExternalCheckStatusLabel(status, direction)}</NoddiBadge>;
